import { useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from '@material-ui/core/TextField';
import IconButton from "@material-ui/core/IconButton";
import SendIcon from "@material-ui/icons/Send";
import DOMPurify from "dompurify";
import { streamWidgetChat } from "../services/widgetChatStream";

const useStyles = makeStyles({
	composer: {
		position: "relative",
		bottom: 0,
		padding: "14px 16px 16px",
		borderTop: "1px solid var(--surface-border)",
		background: "linear-gradient(180deg, rgba(255,255,255,0.92), #fff)",
		flexShrink: 0,
	},
	messageInput: {
		margin: 0,
		fontSize: "var(--active-font-size)",
		fontFamily: "var(--font-sans)",
		width: "100%",

		"& div:first-child": {
			borderRadius: "16px",
			padding: "10px 14px",
			minHeight: "52px",
			border: "1px solid var(--surface-border)",
			background: "#fff",
			boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
			transition: "border-color 0.15s ease, box-shadow 0.15s ease",
			"&.Mui-focused ": {
				borderColor: "var(--active-brand-color)",
				boxShadow: "0 0 0 4px rgba(11, 79, 156, 0.12)",
				background: "#fff",
			},
			"&:focus-within": {
				borderColor: "var(--active-brand-color)",
				boxShadow: "0 0 0 4px rgba(11, 79, 156, 0.12)",
				background: "#fff",
			},
		},

		"& textarea": {
			overflow: "auto !important",
			maxHeight: "120px",
			lineHeight: 1.5,
			fontFamily: "var(--font-sans)",
			letterSpacing: "-0.01em",
		},

		"& fieldset": {
			display: "none"
		}
	},
	icon: {
		color: "#fff",
		fontSize: "18px",
	},
	iconDisabled: {
		color: "var(--color-light-grey)",
	},
	inputButton: {
		height: "40px",
		width: "40px",
		borderRadius: "12px",
		margin: 0,
		background: "var(--active-brand-color)",
		"&:hover": {
			background: "var(--active-brand-color)",
			opacity: 0.92,
		},
		"&:disabled": {
			background: "var(--color-alt-grey)",
		},
		"&:focus": {
			boxShadow: "0 0 0 3px rgba(11, 79, 156, 0.2)",
		},
	},
	"@media (max-width: 325px)": {
		composer: {
			padding: "8px 10px 10px",
		},
	},
	hiddenInput: {
		fontSize: "var(--active-font-size)",
		border: "none",
		outline: "none",
		"&:hover": {
			border: "none",
			outline: "none",
		},
		"&:focus": {
			border: "none",
			outline: "none",
		},
		"&:focus-within": {
			border: "none",
			outline: "none",
		},
		"&:focus-visible": {
			border: "none",
			outline: "none",
		},
		"&:active": {
			border: "none",
			outline: "none",
		},
		"&:target": {
			border: "none",
			outline: "none",
		},
		"&:visited": {
			border: "none",
			outline: "none",
		},
	}
});

const badWordsArr = ['bonk', 'shag', 'slapper', 'tart', 'cunt', 'fuck', 'motherfucker', 'twat', 'snatch', 'pussy', 'punani', 'prick', 'minge', 'knob', 'gash', 'flaps', 'dickhead', 'dick', 'cock', 'clunge', 'bloodclaat', 'bellend', 'bastard', 'tits', 'bitch', 'shit', 'pissed', 'munter', 'feck', 'bullshit', 'bollocks', 'bint', 'arsehole', 'asshole', 'ass', 'cockfoam', 'nigger', 'damn']

function hashingBadWords(str) {
	const myArray = str.split(" ");
	for (let i = 0; i < myArray.length; i++) {
		if (badWordsArr.includes(myArray[i].toLowerCase())) {
			myArray[i] = myArray[i].substring(0, 1) + myArray[i].substring(1, [myArray[i].length - 1]).replace(/[a-zA-Z]/gi, '*') + myArray[i].substring([myArray[i].length - 1]);
		}
	}
	return myArray.join(' ');
}

export default function MessageInput({
	messagesRef,
	online,
	setErrMsg,
	setShowErrorMessage,
	disableBtn,
	messages,
	justFlag,
	firstLoading,
	handleSubmit,
	justOpen,
	handleJustOpen,
	setLoading,
	chatbotIsDraft,
	chatbotId,
	handleFallbackMessage,
	clearFallbackMessage,
	onOptimisticUserMessage,
	onStreamToken,
	onStreamClear,
	brandingTitle,
}) {

	const refMessageInput = useRef(null);
	const [messageValue, setMessageValue] = useState("");
	const [messageSubmitting, setMessageSubmitting] = useState(false);
	const classes = useStyles();

	const handleMessageSubmit = async (e) => {
		e.preventDefault();
		/*Sanitize and validate input from user*/
		let sanitizedInput = DOMPurify.sanitize(messageValue.replace("<", " < ").replace(">", " > "));
		setMessageValue("");
		sanitizedInput = sanitizedInput.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
		const censuredSanitizedInput = hashingBadWords(sanitizedInput);
		setMessageValue(censuredSanitizedInput);
		sessionStorage.setItem(
			"prevMessage",
			`${censuredSanitizedInput}`
		);
		setMessageSubmitting(true);
		const trimmed = censuredSanitizedInput.trim();
		// Show the user bubble first, then the waiting dots
		if (onOptimisticUserMessage) onOptimisticUserMessage(trimmed);
		setLoading(true);

		let streamStarted = false;
		let userMessageId = null;

		try {
			await streamWidgetChat(
				{
					chatbotId,
					text: trimmed,
					parentUrl: document.referrer,
				},
				{
					onMeta: (data) => {
						userMessageId = data?.userMessageId || null;
						if (userMessageId && handleFallbackMessage) {
							handleFallbackMessage(userMessageId);
						}
					},
					onToken: (data) => {
						if (!streamStarted) {
							streamStarted = true;
							setLoading(false);
							if (userMessageId && clearFallbackMessage) {
								clearFallbackMessage(userMessageId);
							}
						}
						if (data?.t != null && onStreamToken) {
							onStreamToken(data.t);
						}
					},
					onDone: (data) => {
						if (userMessageId && clearFallbackMessage) {
							clearFallbackMessage(userMessageId);
						}
						if (onStreamClear) onStreamClear(data);
						setLoading(false);
					},
					onError: (data) => {
						console.error("widgetChatStream error event:", data);
						if (onStreamClear) onStreamClear({ force: true });
						setLoading(false);
						if (setErrMsg) {
							setErrMsg({
								message: data?.message || "Failed to get a response. Please try again.",
								location: "msgInput",
							});
							setShowErrorMessage(true);
						}
					},
				}
			);
			setMessageValue("");
			sessionStorage.setItem("prevMessage", "");
			setMessageSubmitting(false);
			// Stream finished; ensure preview bubble cannot linger under the final answer.
			if (onStreamClear) onStreamClear({ force: true });
			setLoading(false);
		} catch (err) {
			console.error("ERR from widgetChatStream", err);
			setMessageSubmitting(false);
			setMessageValue(sessionStorage.getItem("prevMessage"));
			if (onStreamClear) onStreamClear({ force: true });
			setLoading(false);
			if (setErrMsg) {
				setErrMsg({
					message: err?.message || "Failed to send message. Please try again.",
					location: "msgInput",
				});
				setShowErrorMessage(true);
			}
		}
	};

	useEffect(() => {
		// Check if a prevMessage is present in session to take it when loading the chatbot
		const prevMessage = sessionStorage.getItem("prevMessage");
		const submitMsg = sessionStorage.getItem("submitMsg") === "true";
		console.log("submitMsg", submitMsg)
		if (prevMessage && submitMsg) sessionStorage.setItem("prevMessage", "");
		if (prevMessage && !submitMsg) setMessageValue(prevMessage);
		sessionStorage.setItem("submitMsg", "false");
		console.log("useEffect")
		const errMsg = sessionStorage.getItem("errMsg");
		if (errMsg === "The message exceeds characters limit (256)") {
			setErrMsg({
				message:
					"The message exceeds characters limit (256)",
				location: "msgInput",
			});
			setShowErrorMessage(true);
		} else if (errMsg === "Invalid message") {
			setErrMsg({
				message: "Invalid message",
				location: "msgInput",
			});
			setShowErrorMessage(true);
		}
	}, []);

	const handleFormSubmit = (e) => {
		const options = {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false, // 24-hour format
			timeZoneName: 'short',
			fractionalSecondDigits: 3 // Milliseconds
		};
		const formattedDate = new Date(Date.now()).toLocaleString('en-US', options);
		console.log("FormSubmit", formattedDate)
		sessionStorage.setItem("submitMsg", "true");
		console.log("handleFormSubmit", handleFormSubmit)
		e.preventDefault();
		handleMessageSubmit(e);
		refMessageInput.current.focus();
		handleSubmit();
		handleJustOpen();
	};

	const placeholder = brandingTitle
		? `Message ${brandingTitle}…`
		: "Ask about policies, properties, or procedures…";

	return (
		<div className={classes.composer}>
			<form onSubmit={handleFormSubmit}>
				{/* <Input */}
				<TextField
					id="text-field-multiline"
					multiline
					maxrows={6}
					className={classes.messageInput}
					aria-label="chatbot message input"
					variant="outlined"
					disabled={messageSubmitting || !online || chatbotIsDraft}
					placeholder={placeholder}
					value={messageSubmitting ? "" : messageValue}
					onChange={(e) => {
						let userMsg = e.target.value;
						/*Sanitize and validate input from user*/
						let sanitizedUserMsg = DOMPurify.sanitize(userMsg)
						if (userMsg.length > 256) {
							setErrMsg({
								message:
									"The message exceeds characters limit (256)",
								location: "msgInput",
							});
							setShowErrorMessage(true);
							//To save the message if the chatbot is minimized
							sessionStorage.setItem(
								"errMsg",
								"The message exceeds characters limit (256)"
							);
						} else if (userMsg && !sanitizedUserMsg) {
							setErrMsg({
								message: "Invalid message",
								location: "msgInput",
							});
							setShowErrorMessage(true);
							//To save the message if the chatbot is minimized
							sessionStorage.setItem(
								"errMsg",
								"Invalid message"
							);
						} else {
							setErrMsg({
								message: "",
								location: "",
							});
							setShowErrorMessage(false);
							//To clear the message
							sessionStorage.removeItem("errMsg");
						}
						setMessageValue(userMsg);
						//To save the message if the chatbot is minimized
						sessionStorage.setItem(
							"prevMessage",
							`${userMsg}`
						);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							if (!messageValue.replace(/\s/g, "").length ||
								!online ||
								messageSubmitting ||
								disableBtn || messageValue.length > 256 || sessionStorage.getItem("errMsg")) {
								console.log("Invalid message")
							} else {
								console.log('onKeyDown else');
								handleFormSubmit(e)
							}
						}
					}}
					inputRef={(input) => input && !justFlag && !justOpen && input.focus()}
					InputProps={{
						endAdornment:
							<InputAdornment position="end">
								<IconButton
									type="submit"
									className={classes.inputButton}
									disabled={
										!messageValue.replace(/\s/g, "").length ||
										!online ||
										messageSubmitting ||
										disableBtn || messageValue.length > 256 || sessionStorage.getItem("errMsg")
									}
									aria-label="send message"
									edge="end"
								>
									<SendIcon
										className={
											messageValue.replace(/\s/g, "")
												.length &&
												online &&
												!messageSubmitting &&
												!disableBtn
												? classes.icon
												: classes.iconDisabled
										}
									/>
								</IconButton>
							</InputAdornment>,
						"aria-label": "chatbot message input",
					}}
				/>
				{/* ></Input> */}
			</form>
			{/* Add to get the focus when a new message is submitted and keep the softkeyboard of the phone open to avoid it flickering because close and open quikly
			This modification does not affect the app when running on PC so can be implemented in all condition */}
			{/* <input type="number" tabIndex="-1" ref={refMessageInput} style={{ */}
			<input 
				type="text" 
				tabIndex="-1" 
				ref={refMessageInput} 
				aria-label="Hidden input for maintaining keyboard focus"
				aria-hidden="true"
				role="presentation"
				style={{
					position: 'absolute',
					right: '0%',
					top: '40px',
					width: '0%',
					height: '0px',
					margin: "auto",
					zIndex: "-1",
					fontSize: "16px",
				}}
				className={classes.hiddenInput}
			/>
		</div>
	);
}
